import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Calendar, Users, TrendingUp, Heart, MessageCircle, Share2, BarChart3, Target, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const SocialMedia = () => {
  const project = {
    title: 'FreshBite - Sosyal Medya Yönetimi',
    client: 'FreshBite Restoran Zinciri',
    duration: '12 Ay',
    category: 'Sosyal Medya Yönetimi',
    year: '2025',
    heroImage: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1200&h=600&fit=crop',
    description: 'FreshBite restoran zinciri için kapsamlı sosyal medya yönetimi ve içerik stratejisi uyguladık. Instagram, TikTok ve Facebook platformlarında marka bilinirliğini artırarak müşteri etkileşimini 3 kat artırdık.',
    challenge: 'FreshBite, yoğun rekabet ortamında sosyal medyada görünürlük kazanmakta zorlanıyordu. Mevcut içerikler düşük etkileşim alıyordu ve marka kimliği tutarsızdı.',
    solution: 'Hedef kitle analizi yaparak platform bazlı içerik stratejileri geliştirdik. Video içerik ağırlıklı bir yaklaşım benimseyerek, kullanıcı tarafından üretilen içerikleri teşvik ettik.'
  };

  const stats = [
    { label: 'Takipçi Artışı', value: '+245%', icon: Users },
    { label: 'Etkileşim Oranı', value: '8.5%', icon: Heart },
    { label: 'Video Görüntüleme', value: '2.5M+', icon: TrendingUp },
    { label: 'Marka Bilinirliği', value: '+180%', icon: Target }
  ];

  const platforms = [
    { name: 'Instagram', followers: '125K', engagement: '9.2%', color: 'from-pink-500 to-purple-500' },
    { name: 'TikTok', followers: '89K', engagement: '12.5%', color: 'from-cyan-500 to-blue-500' },
    { name: 'Facebook', followers: '45K', engagement: '4.8%', color: 'from-blue-500 to-indigo-500' }
  ];

  const contentTypes = [
    { type: 'Reels / TikTok', count: '240+', desc: 'Kısa format video içerikler' },
    { type: 'Story Highlights', count: '50+', desc: 'Öne çıkan hikaye koleksiyonları' },
    { type: 'Carousel Posts', count: '180+', desc: 'Kaydırmalı görsel içerikler' },
    { type: 'UGC Kampanyaları', count: '12', desc: 'Kullanıcı içerik kampanyaları' }
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1482049016gy6-74ac62b6e44a?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=600&h=600&fit=crop'
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/?scrollTo=portfolio"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Portfolyoya Dön
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-pink-500/20 text-pink-400 text-sm font-medium rounded-full">
                  {project.category}
                </span>
                <span className="text-gray-500">{project.year}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {project.title}
              </h1>

              <p className="text-xl text-gray-400 mb-8">{project.description}</p>

              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Müşteri</span>
                  <p className="text-white font-medium">{project.client}</p>
                </div>
                <div>
                  <span className="text-gray-500">Süre</span>
                  <p className="text-white font-medium">{project.duration}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src={project.heroImage}
                alt={project.title}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 shadow-xl">
                <MessageCircle className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">3x</div>
                <div className="text-pink-100 text-sm">Etkileşim Artışı</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-pink-500/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Zorluk</h3>
              <p className="text-gray-400">{project.challenge}</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Çözüm</h3>
              <p className="text-gray-400">{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Performance */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Platform <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Performansı</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 hover:border-pink-500/50 transition-all">
                <div className={`w-full h-2 bg-gradient-to-r ${platform.color} rounded-full mb-4`}></div>
                <h3 className="text-xl font-bold text-white mb-4">{platform.name}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Takipçi</span>
                    <span className="text-white font-medium">{platform.followers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Etkileşim Oranı</span>
                    <span className="text-cyan-400 font-medium">{platform.engagement}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Types */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Üretilen <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">İçerikler</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {contentTypes.map((content, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 text-center hover:border-pink-500/50 transition-all">
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent mb-2">
                  {content.count}
                </div>
                <div className="text-white font-medium mb-1">{content.type}</div>
                <div className="text-gray-500 text-sm">{content.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            İçerik <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Galerisi</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-2xl aspect-square">
                <img
                  src={image}
                  alt={`Content ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                  <div className="flex gap-4 text-white">
                    <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> 2.4K</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> 156</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Sosyal Medyanızı
            <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent"> Büyütelim</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Markanızın sosyal medya varlığını güçlendirmek için bizimle iletişime geçin.
          </p>
          <Button 
            onClick={() => window.location.href = '/iletisim'}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-6 text-lg rounded-xl"
          >
            Ücretsiz Danışmanlık Al
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMedia;
